import { uniq, uniqBy, keyBy } from "./collectionUtils"

/**
 * Insights für die Statistics-Seite — aus Shop-Betreiber-Perspektive.
 * Alle Funktionen liefern direkt nutzbare, selbsterklärende Werte.
 *
 * @author pbuerger
 */
const PPStatisticsInsights = {
    competitorShops(shopStats) {
        return (shopStats || []).filter((s) => s && s.shopId > 0)
    },

    stockTotal(shop) {
        return (
            (shop.countStockGreenProducts || 0) +
            (shop.countStockYellowProducts || 0) +
            (shop.countStockRedProducts || 0)
        )
    },

    priceTotal(shop) {
        return (shop.productsPriceLower || 0) + (shop.productsPriceEqual || 0) + (shop.productsPriceHigher || 0)
    },

    /**
     * Preisposition je Shop: % günstiger/gleich/teurer + Ø-Abstände.
     * Sortiert nach größtem Handlungsbedarf (höchster Teurer-Anteil zuerst).
     */
    pricePositionByShop(shopStats, gaps) {
        const gapByShop = keyBy(gaps || [], (g) => String(g.shopId))
        return this.competitorShops(shopStats)
            .map((shop) => {
                const total = this.priceTotal(shop)
                const cheaper = shop.productsPriceLower || 0
                const equal = shop.productsPriceEqual || 0
                const expensive = shop.productsPriceHigher || 0
                const gap = gapByShop[String(shop.shopId)]
                return {
                    shop,
                    total,
                    cheaper,
                    equal,
                    expensive,
                    cheaperPct: total > 0 ? Math.round((100 * cheaper) / total) : 0,
                    equalPct: total > 0 ? Math.round((100 * equal) / total) : 0,
                    expensivePct: total > 0 ? Math.round((100 * expensive) / total) : 0,
                    avgGapCheaper: Number(gap?.avgGapWhenCheaper) || 0,
                    avgGapExpensive: Number(gap?.avgGapWhenMoreExpensive) || 0
                }
            })
            .sort((a, b) => b.expensivePct - a.expensivePct)
    },

    /**
     * Verfügbarkeits-Chancen: Shops wo Konkurrenz schlecht liefern kann (hohes Gelb+Rot).
     * Je höher der Nicht-Grün-Anteil, desto größer die Chance.
     */
    availabilityOpportunities(shopStats) {
        return this.competitorShops(shopStats)
            .map((shop) => {
                const total = this.stockTotal(shop)
                const green = shop.countStockGreenProducts || 0
                const yellow = shop.countStockYellowProducts || 0
                const red = shop.countStockRedProducts || 0
                const notGreenPct = total > 0 ? Math.round((100 * (yellow + red)) / total) : 0
                return {
                    shop,
                    total,
                    green,
                    yellow,
                    red,
                    greenPct: total > 0 ? Math.round((100 * green) / total) : 0,
                    notGreenPct
                }
            })
            .sort((a, b) => b.notGreenPct - a.notGreenPct)
    },

    /**
     * Anteil Produkte mit Preisänderung je Shop (aus Preistrends).
     * rising + falling / totalCount.
     */
    priceActivityByShop(trendByShop) {
        return (trendByShop || [])
            .filter((entry) => entry && (entry.shopId || 0) > 0 && (entry.totalCount || 0) > 0)
            .map((entry) => {
                const mapped = entry.totalCount || 0
                const changes = (entry.risingCount || 0) + (entry.fallingCount || 0)
                const per100 = mapped > 0 ? Math.round((changes / mapped) * 100) : 0
                const shop = {
                    shopId: entry.shopId,
                    name: entry.shopName,
                    shortName: entry.shopName,
                    lang: entry.lang
                }
                return { shop, changes, mapped, per100 }
            })
            .sort((a, b) => b.per100 - a.per100)
    },

    /**
     * Kategorie-Hotspots: Wo bewegen sich Preise am meisten?
     */
    categoryHotspots(trendByCategory, limit = 8) {
        return (trendByCategory || [])
            .filter((c) => c && (c.totalCount || 0) > 0)
            .map((c) => {
                const total = c.totalCount || 1
                const rising = c.risingCount || 0
                const falling = c.fallingCount || 0
                return {
                    ...c,
                    risingShare: Number(((100 * rising) / total).toFixed(1)),
                    fallingShare: Number(((100 * falling) / total).toFixed(1)),
                    movementPct: Math.round((100 * (rising + falling)) / total)
                }
            })
            .sort((a, b) => b.movementPct - a.movementPct)
            .slice(0, limit)
    },

    /**
     * Verfügbarkeits-Trend: Grün-Anteil je Shop über die Zeit.
     */
    stockTrendByShop(history, langFilter) {
        const filtered = this.filterByLang(history || [], langFilter)
        const shops = uniqBy(
            filtered.map((e) => ({ shopId: e.shopId, name: e.shortName || e.shopName, lang: e.lang })),
            (s) => `${s.shopId}-${s.lang}`
        )
        const dates = uniq(filtered.map((e) => e.date)).sort((a, b) => String(a).localeCompare(String(b)))
        const series = shops.map((shop) => ({
            name: this.shopLabel(shop),
            data: dates.map((d) => {
                const row = filtered.find(
                    (e) => e.shopId === shop.shopId && e.lang === shop.lang && e.date === d
                )
                const total = row ? (row.green || 0) + (row.yellow || 0) + (row.red || 0) : 0
                return total > 0 ? Math.round((100 * (row.green || 0)) / total) : null
            })
        }))
        return { dates: dates.map((d) => this.formatShortDate(d)), series }
    },

    filterByLang(items, langFilter, langKey = "lang") {
        if (!langFilter || langFilter.length === 0) return items || []
        const langs = langFilter.map((x) => x.value)
        return (items || []).filter((item) => langs.includes(item[langKey]))
    },

    shopLabel(shop) {
        if (!shop) return ""
        const name = shop.name || shop.shortName || ""
        const lang = (shop.lang || "").toUpperCase()
        return shop.shopId === 0 || !shop.lang ? name : `${name} (${lang})`
    },

    formatShortDate(isoDate) {
        if (!isoDate) return ""
        const parts = String(isoDate).split("-")
        return parts.length === 3 ? `${parts[2]}.${parts[1]}` : isoDate
    },

    formatEuro(value) {
        if (value == null || Number.isNaN(Number(value))) return "–"
        return `${Number(value).toLocaleString("de-DE", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`
    },

    formatPct(value) {
        return `${Math.round(value || 0)}%`
    },

    /**
     * Alias for backward compatibility
     */
    shopPriceBalances(shopStats, gaps) {
        return this.pricePositionByShop(shopStats, gaps).map((r) => ({
            shop: r.shop,
            total: r.total,
            cheaper: r.cheaper,
            equal: r.equal,
            expensive: r.expensive,
            cheaperShare: r.cheaperPct,
            equalShare: r.equalPct,
            expensiveShare: r.expensivePct,
            avgGapCheaper: r.avgGapCheaper,
            avgGapExpensive: r.avgGapExpensive
        }))
    },

    /**
     * Volatility ranking for backward compatibility
     */
    volatilityRanking(shopStats) {
        return this.priceActivityByShop(shopStats)
    },

    /**
     * Stock history by shop as line series
     */
    stockHistoryByShopSeries(history, langFilter) {
        const result = this.stockTrendByShop(history, langFilter)
        return {
            shortDates: result.dates,
            series: result.series
        }
    }
}

export default PPStatisticsInsights
